// Page Synthese - V4 Multi-metiers
// Affiche la synthese partielle (apres M1+Q2) ou finale (tous metiers completes)
// Design V4 : CTA engageant pour encourager la completion

import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Button, Card, CardContent, CardHeader, CardTitle } from '../components/ui'
import { trackEvent } from '../lib/analytics'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'
import { Progress } from '../components/ui/progress'
import { Sparkles, ArrowRight } from 'lucide-react'
import RadarChart, { RadarLegend, type ScoresAxes, type MaxParAxe } from '../components/results/RadarChart'
import RadarChartManagement, {
  RadarLegendManagement,
  MANAGEMENT_AXES,
  type ScoresManagement,
} from '../components/results/RadarChartManagement'
import { api } from '../services/api'

interface SelectedMetier {
  code: string
  nom: string
  ordre: number
}

interface LocationState {
  metiersSelectionnes?: SelectedMetier[]
  metierIds?: string[]
  completedMetierIndex?: number
  isPartial?: boolean
}

interface ReponseData {
  questionId: string
  metierId?: string
  valeur: number
}

// Axes HALTES
const HALTES_AXES = ['H', 'A', 'LT', 'E', 'S'] as const

export default function SynthesePage() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // Charger les données du diagnostic (depuis state ou IndexedDB)
  const { metierIds, metiersSelectionnes, diagnosticState, isLoadingData } = useDiagnosticData(diagnosticId)

  // Récupérer les données supplémentaires depuis le state de navigation
  const locationState = location.state as LocationState | undefined
  const completedMetierIndex = locationState?.completedMetierIndex ?? 0
  const isPartial = locationState?.isPartial ?? false

  const [reponses, setReponses] = useState<ReponseData[]>([])
  const [questionsQ1, setQuestionsQ1] = useState<any[]>([])
  const [questionsQ2, setQuestionsQ2] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Charger la progression du diagnostic pour le stepper
  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  // Sur la synthèse intermédiaire, Q2 a toujours été complété
  const steps = useDiagnosticSteps('resultats', diagnosticId, {
    totalMetiers: metierIds.length,
    currentMetierIndex: completedMetierIndex,
    q2Completed: true,
    metiersSelectionnes: metiersSelectionnes.map(m => ({ code: m.code, nom: m.nom })),
  }, diagnosticProgress)

  // Metiers completes jusqu'ici
  const completedMetierIds = metierIds.slice(0, completedMetierIndex + 1)
  const completedMetiers = metiersSelectionnes.slice(0, completedMetierIndex + 1)

  // Prochain metier a completer (si partial)
  const nextMetierIndex = completedMetierIndex + 1
  const nextMetier = metiersSelectionnes[nextMetierIndex]
  const hasMoreMetiers = nextMetierIndex < metierIds.length
  const remainingMetiers = metierIds.length - completedMetierIds.length

  useEffect(() => {
    async function loadData() {
      if (!diagnosticId) return

      setIsLoading(true)

      try {
        const [diagnostic, q1, q2] = await Promise.all([
          api.getDiagnostic(diagnosticId),
          api.getQuestionsQ1(),
          api.getQuestionsQ2(),
        ])

        setReponses((diagnostic.reponses || []).map((r: any) => ({
          ...r,
          metierId: r.metier?.code || r.metierId,
        })))
        setQuestionsQ1(q1)
        setQuestionsQ2(q2)
      } catch (error) {
        console.error('Erreur chargement donnees synthese:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [diagnosticId])

  // Calculer les scores Q1 par metier et par axe (en points)
  const scoresQ1ByMetier = useMemo(() => {
    const result = new Map<string, { scores: ScoresAxes; maxScores: MaxParAxe }>()

    completedMetierIds.forEach((metierId) => {
      const scores: ScoresAxes = { H: 0, A: 0, LT: 0, E: 0, S: 0 }
      const counts: ScoresAxes = { H: 0, A: 0, LT: 0, E: 0, S: 0 }

      const metierReponses = reponses.filter(r => r.metierId === metierId)

      metierReponses.forEach((reponse) => {
        const question = questionsQ1.find(q => q.id === reponse.questionId)
        if (question) {
          const axe = (question.axe || question.axeHaltes) as keyof ScoresAxes
          if (axe && HALTES_AXES.includes(axe as typeof HALTES_AXES[number])) {
            scores[axe] += reponse.valeur
            counts[axe] += 1
          }
        }
      })

      const maxScores: MaxParAxe = {
        H: counts.H * 3,
        A: counts.A * 3,
        LT: counts.LT * 3,
        E: counts.E * 3,
        S: counts.S * 3,
      }

      result.set(metierId, { scores, maxScores })
    })

    return result
  }, [completedMetierIds, reponses, questionsQ1])

  // Calculer les scores Q2 (management) par axe (en points)
  const scoresQ2Data = useMemo(() => {
    const scores: ScoresManagement = {
      orientations: 0,
      roles: 0,
      gestion: 0,
      animation: 0,
      bilan: 0,
      audits: 0,
    }
    const counts: ScoresManagement = {
      orientations: 0,
      roles: 0,
      gestion: 0,
      animation: 0,
      bilan: 0,
      audits: 0,
    }

    const q2Reponses = reponses.filter(r => !r.metierId)

    q2Reponses.forEach((reponse) => {
      const question = questionsQ2.find(q => q.id === reponse.questionId)
      if (question && question.sousMethode) {
        const axeConfig = MANAGEMENT_AXES.find(a => a.code === question.sousMethode)
        if (axeConfig) {
          const axeKey = axeConfig.key
          scores[axeKey] += reponse.valeur
          counts[axeKey] += 1
        }
      }
    })

    const maxScores: ScoresManagement = {
      orientations: counts.orientations * 3,
      roles: counts.roles * 3,
      gestion: counts.gestion * 3,
      animation: counts.animation * 3,
      bilan: counts.bilan * 3,
      audits: counts.audits * 3,
    }

    return { scores, maxScores }
  }, [reponses, questionsQ2])

  // Score Q2 global
  const scoreQ2Global = useMemo(() => {
    const totalScore = Object.values(scoresQ2Data.scores).reduce((a, b) => a + b, 0)
    const totalMax = Object.values(scoresQ2Data.maxScores).reduce((a, b) => a + b, 0)
    return { score: totalScore, max: totalMax }
  }, [scoresQ2Data])

  // Navigation
  const handleContinueNextMetier = () => {
    trackEvent('continuer metier suivant')
    navigate(`/diagnostic/${diagnosticId}/q1`, {
      state: {
        ...diagnosticState,
        currentMetierIndex: nextMetierIndex,
      },
    })
  }

  const handleSeeResults = () => {
    trackEvent('voir les resultats')
    navigate(`/diagnostic/${diagnosticId}/resultats`, {
      state: { ...diagnosticState, isPartial },
    })
  }

  const handleBack = () => {
    navigate(`/diagnostic/${diagnosticId}/q2`, {
      state: { ...diagnosticState, completedMetierIndex },
    })
  }

  if (isLoading || isLoadingData) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}

        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Calcul de la synthèse...</p>
          </div>
        </div>
      </DiagnosticLayout>
    )
  }

  return (
    <DiagnosticLayout
      steps={steps}
      onBack={handleBack}
      onNext={isPartial && hasMoreMetiers ? handleContinueNextMetier : handleSeeResults}
      backLabel="Retour au questionnaire"
      nextLabel={isPartial && hasMoreMetiers ? `Continuer avec ${nextMetier?.nom}` : 'Voir les résultats finaux'}

      diagnosticState={diagnosticState}
    >
      {/* Header de page */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isPartial ? 'Synthèse Intermédiaire' : 'Synthèse des Résultats'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Résultats pour {completedMetiers.map(m => m.nom).join(', ')}
            </p>
          </div>
        </div>
      </div>

      {/* CTA Principal si partial — encourageant, vert SCOP */}
      {isPartial && hasMoreMetiers && (
        <Card className="border-primary/30 bg-primary/5 mb-8">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="size-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-foreground">
                    Vous y êtes presque !
                  </h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Plus que <span className="font-semibold text-foreground">{remainingMetiers} métier{remainingMetiers > 1 ? 's' : ''}</span> à évaluer
                    pour débloquer vos casques et votre plan d'actions.
                  </p>
                  <div className="flex items-center gap-3 mt-3">
                    <Progress
                      value={Math.round((completedMetierIds.length / metierIds.length) * 100)}
                      className="h-2 flex-1 max-w-48"
                    />
                    <span className="text-xs text-muted-foreground">
                      {completedMetierIds.length}/{metierIds.length} métiers
                    </span>
                  </div>
                </div>
              </div>
              <Button onClick={handleContinueNextMetier} className="flex-shrink-0">
                Continuer avec {nextMetier?.nom}
                <ArrowRight className="size-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grille des radars - Layout 2 colonnes */}
      <div className="grid gap-6 lg:grid-cols-2 mb-8">
        {/* Radars Q1 par metier */}
        {completedMetiers.map((metier) => {
          const data = scoresQ1ByMetier.get(metier.code)
          if (!data) return null

          const totalScore = Object.values(data.scores).reduce((a, b) => a + b, 0)
          const totalMax = Object.values(data.maxScores).reduce((a, b) => a + b, 0)

          return (
            <Card key={metier.code}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">
                    {metier.ordre}
                  </span>
                  {metier.nom} - Risques
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadarChart scores={data.scores} maxScores={data.maxScores} />
                <RadarLegend scores={data.scores} maxScores={data.maxScores} compact />
                <div className="mt-3 text-center">
                  <span className="text-lg font-bold text-primary">
                    Total : {totalScore}/{totalMax} pts
                  </span>
                </div>
              </CardContent>
            </Card>
          )
        })}

        {/* Radar Q2 - Management */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </span>
              Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RadarChartManagement scores={scoresQ2Data.scores} maxScores={scoresQ2Data.maxScores} />
            <RadarLegendManagement scores={scoresQ2Data.scores} maxScores={scoresQ2Data.maxScores} compact />
            <div className="mt-3 text-center">
              <span className="text-lg font-bold text-primary">
                Total : {scoreQ2Global.score}/{scoreQ2Global.max} pts
              </span>
              <p className="text-xs text-muted-foreground mt-1">
                Ce score est commun à tous vos métiers.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Note de reprise */}
      <p className="text-sm text-muted-foreground text-center">
        Vous pouvez revenir à cette synthèse à tout moment depuis votre diagnostic.
      </p>
    </DiagnosticLayout>
  )
}
